// eslint-disable-next-line max-len
/* eslint-disable implicit-arrow-linebreak,no-param-reassign,arrow-body-style,no-nested-ternary,import/named */
import {
  cloneElement,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
  type ComponentType,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react'
import {
  Grid,
  ListItem as MuiListItem,
  ListItemButton as MuiListItemButton,
  type GridSize,
} from '@mui/material'
import {
  Virtuoso,
  type VirtuosoHandle,
  type Components,
  type StateSnapshot,
} from 'react-virtuoso'
import { def, isArr, PlatformEvent, repeat } from 'lib/util'
import type { PaginationResult } from '@platform/react/hook/usePagination'
import { preload, useMemoRef, usePagination, useSockets, useStyles } from 'platform/react/hook'
import NoDataHOC from 'platform/react/hoc/nodata'
import { listRowClassName } from 'platform/react/hoc/list/cell'
import ErrorBoundary from 'ui/Error'
import SubHeader from 'ui/Element/List/Header/Sub'
import Header from 'ui/Element/List/Header/Main'

const DEFAULT_ROW_HEIGHT = 80
const DEFAULT_SEGMENT_HEIGHT = 56

// TODO: Eventually, each model should export a model specific type and this
//       should be a union of all of those
export interface ListDataItem {
  /** unique identifier for the item */
  key: string
  /** the `value` of the item */
  value?: Record<string, any>
  /** the `refs` of the item */
  refs?: Record<string, ListDataItem[]>
  /** the `meta` of the item */
  meta?: unknown
}

export type SegmentOptions = string | {
  /** view name used to render the segment */
  view: string
  /** placeholder view while loading */
  placeholder?: string
}

// only used on mobile
// It's declared here because it's part of `ItemOptions`
// which is declared here and used for both platforms.
export type CellSize = number | string | boolean

export interface CellSizes {
  ms?: CellSize
  xs?: GridSize
  sm?: GridSize
  md?: GridSize
  lg?: GridSize
  xl?: GridSize
}

export interface Cell {
  /** component used to render the cell */
  View: ComponentType<any>
  /** props forwarded to the cell view */
  cellProps: { key?: string } & Record<string, unknown>
  /** responsive sizes for the cell */
  sizes: CellSizes
}

export interface ListItem {
  /** unique identifier for the row */
  key: string
  /** cells rendered within the row */
  cells: Cell[]
  /** data for the row */
  data?: ListDataItem
  /** whether the row is a segment header */
  isSegment?: boolean
  /** whether the row is first in its group */
  isFirstOfGroup?: boolean
  /** whether the row is last in its group */
  isLastOfGroup?: boolean
}

export interface ItemOptions {
  /** view name used to render the cell */
  view: string
  /** placeholder view while loading */
  placeholder?: string
  /** options for the cell */
  options?: {
    /** keys from `value` and `refs` to pass to the cell */
    keys?: Record<string, boolean>
    ms?: CellSize
    xs?: GridSize
    sm?: GridSize
    md?: GridSize
    lg?: GridSize
    xl?: GridSize
    /** placeholder options for the cell */
    placeholder?: PlaceholdersOptions
    /** template name for the cell */
    template?: string
  }
}

export interface PlaceholdersOptions {
  /** the number of placeholder rows to create */
  count?: number
  /** the height of all placeholders */
  height?: number
  /** the height of all placeholders */
  segmentHeight?: (number | string) | (number | string)[]
  /** the height and width of icon placeholders */
  iconSize?: number
  /** the height of the block placeholders */
  blockHeight?: number
  /** the width of the block placeholders */
  blockWidth?: number
  /** the number of lines of text placeholders */
  textLines?: number
  /** the width of the lines of text placeholders */
  textLineWidth?: number[]
  /** the height of the lines of text placeholders */
  textLineHeight?: number[]
}

export interface ListRowDetail {
  /** key of the row */
  key: string
  /** data of the row */
  item?: ListDataItem
}

export interface BaseListPageDetail {
  /** data for the page */
  data: ListDataItem[]
  /** current page token */
  page: string | null
  /** whether the data is unsegmented */
  unsegmented?: boolean
}

export interface ListPageDetail extends BaseListPageDetail {
  /** current scroll position snapshot */
  scrollPosition: StateSnapshot | null
}

export interface BaseListEvents {
  /** a function to call to obtain the items to display */
  onOpen: (e: PlatformEvent<{ page: string | null }>) =>
    Promise<PaginationResult> | PaginationResult
  /** an event handler to execute whenever the row is clicked */
  onClick?: (e: PlatformEvent<ListRowDetail>) => void
  /**
   * an event handler to execute whenever the row is clicked. if this handler is
   * present, it will replace the `onClick` handler. It's intended to be used if
   * the list should behave as a normal input field that has a value that can be
   * changed
   */
  onChange?: (e: PlatformEvent<ListRowDetail>) => void
  // Web curries this as `(data) => (event) => void` while mobile calls it
  // directly as a long-press handler, hence the permissive signature.
  // TODO: Do we need to unify this? Why does it differ?
  onContextMenu?: (data?: ListDataItem) => RowProps['onContextMenu']
  /** subscription handler for the list */
  list?: (e: PlatformEvent<null>) => string
  /** subscription handler for a row */
  item?: (e: PlatformEvent<{ key: string }>) => ListDataItem
  /** subscription handler for a cell */
  cell?: (e: PlatformEvent<{ key: string }>) => { data: Number, addData?: Function }
  /** publication handler for the list */
  onPublication?: (e: PlatformEvent<{ id: string }>) => void
  /** handler returning filter options */
  filter?: (e: PlatformEvent<null>) => string
  /** handler returning query options */
  query?: (e: PlatformEvent<{ value: string } | null>) => string
}

export interface ListEvents extends BaseListEvents {
  /** called whenever the list obtains new data */
  onPage?: (e: PlatformEvent<ListPageDetail>) => void
}

export interface BaseListProps {
  /** an array of objects defining view and props of each cell */
  structure: ItemOptions[]
  /** how many items to fetch on each request */
  limit?: number
  /** initial data to be displayed by the list */
  data?: ListDataItem[]
  /** hash of the next page to be obtained by the list */
  page?: string | null
  /** selected value. Its row will be selected */
  value?: string | null
  /**
   * disabled state of the list. Can either be a boolean, in which case the whole
   * list will be disabled if it's `true`, or it can be a list of ids, in which
   * case the corresponding rows will be disabled
   */
  disabled?: boolean | string[]
  /** fallback to display for suspended children */
  placeholders?: PlaceholdersOptions
  /** the type of document displayed by the list */
  documentType?: string
  /** an array of labels to be used as column headers */
  label?: string | string[]
  /** the path to a header element to be loaded on runtime */
  header?: string
  /** a label to be passed to the header element */
  headerLabel?: string
  /** the appearance variant to be used by the component */
  variant?: string
  /** the amount of space between each column */
  spacing?: number
  /** row padding */
  padding?: number | string
  /** optional fixed row height */
  rowHeight?: number
  /** an array defining the grid width of each column */
  size?: CellSize[]
  /** attribute names mapped to view paths */
  segments?: Record<string, SegmentOptions>
  /** attribute names mapped to labels */
  segmentLabel?: Record<string, Record<string, string>>
  /** whether to ignore `props.segment` */
  unsegmented?: boolean
  /** a component to be displayed when there is no data */
  noData?: ReactElement
  /** whether the component must be hidden if there is no data */
  autoHide?: boolean
  /** whether the list sizes to its content */
  autoHeight?: boolean
  /** if a row can be clicked */
  action?: boolean
  /** number of items to overscan */
  overscanItemCount?: number
  /** whether to force a rerender of the list */
  update?: Object
  /** whether to force a reload of the list's elements */
  reload?: Object
  /**
   * whether the scroll bars should be padded. If true, they'll be padded by `contentGap`,
   * giving the illusion that the scroll bars are attached to the {@link ContentGrid}.
   */
  scrollPadding?: boolean,
  /** whether the list was rendered by entering a search term */
  fromSearch?: boolean
  /** child elements */
  children?: ReactNode
}

export interface ListProps extends BaseListProps {
  /** a reference to the root element */
  ref?: any
  /** an object containing the component's event handlers */
  events: ListEvents
  /** function used for iterating over the structure */
  iterator?: (data: ListDataItem[], props: ListProps) => ListItem[]
  /** vertical alignment of the row's cells */
  align?: string
  /** the horizontal alignment of all cells' content */
  justify?: string
  /** initial scroll position */
  scrollPosition?: StateSnapshot
}

export interface BaseSegmentsPlaceholdersProps {
  /** the list's segments configuration */
  segments?: Record<string, SegmentOptions>
  /** an object to configure all rows */
  placeholders?: PlaceholdersOptions
  /** whether to ignore `segments` */
  unsegmented?: boolean
}

export interface SegmentsPlaceholdersProps extends BaseSegmentsPlaceholdersProps {
  /** segment height */
  height: PlaceholdersOptions['segmentHeight']
}

export interface BasePlaceholderProps {
  /** an object defining the props of each row */
  structure: ItemOptions[]
  /** an object to configure all rows */
  placeholders?: PlaceholdersOptions
  /** component used to render the placeholder */
  component?: ComponentType<any>
  /** the amount of space between each column */
  spacing?: number
  /** an array with the grid width of each cell */
  size?: CellSize[]
}

export interface PlaceholderProps extends BasePlaceholderProps {
  /** the horizontal alignment the cells' content */
  justify?: string
}

export interface BasePlaceholdersProps {
  /** the number of placeholder rows to create */
  count?: number
}

export interface PlaceholdersProps extends PlaceholderProps, BasePlaceholdersProps {}

interface ListContext {
  /** selected value. Its row will be selected */
  value: RowProps['value']
  /**
   * disabled state of the list. Can either be a boolean, in which case the whole
   * list will be disabled if it's `true`, or it can be a list of ids, in which
   * case the corresponding rows will be disabled
   */
  disabled: RowProps['disabled']
  /** pre-defined height of a segment */
  segmentHeight: PlaceholdersOptions['segmentHeight']
  /** whether a transition is pending */
  pending: boolean
  /** whether data is loading */
  loading: boolean
  /** whether the list has data */
  hasData: boolean
  /** whether this is the first load */
  firstLoad: boolean
  /** all remaining list properties */
  props: ListProps
  /** the element to display when there is no data */
  noDataPlaceholder: ReactElement
  /** whether the scroll bars should be padded */
  scrollPadding: boolean
}

export interface BaseRowProps {
  /** a data item processed by {@link createItems} */
  item: ListItem
  /** amount of spacing between each cell */
  spacing?: number
}

export interface RowProps extends BaseRowProps {
  /** selected value. Its row will be selected */
  value?: string | null
  /**
   * disabled state of the list. Can either be a boolean, in which case the whole
   * list will be disabled if it's `true`, or it can be a list of ids, in which
   * case the corresponding rows will be disabled
   */
  disabled?: boolean | string[]
  /** the name of the row variant styles */
  variant?: string
  /** vertical alignment of the row's cells */
  align?: string
  /** horizontal alignment of the row's cells */
  justify?: string
  /** pre-defined height of a row */
  height: number
  /** pre-defined height of a segment */
  segmentHeight: PlaceholdersOptions['segmentHeight']
  /** row padding */
  padding?: number | string
  /** an event handler to execute whenever the row is clicked */
  onClick?: (e: PlatformEvent<ListRowDetail>) => void
  /**
   * an event handler to execute whenever the row is clicked. if this handler is
   * present, it will replace the `onClick` handler. It's intended to be used if
   * the list should behave as a normal input field that has a value that can be
   * changed
   */
  onChange?: (e: PlatformEvent<ListRowDetail>) => void
  /** an event handler to execute whenever the context menu is invoked */
  onContextMenu?: (e: MouseEvent<HTMLElement> | PlatformEvent<ListRowDetail>) => void
}

const sx = {
  root: {
    position: 'relative',
    backgroundColor: 'inherit',
    paddingTop: 0,
    paddingBottom: 0,

    width: '100%',
    maxWidth: '100%',

    flexBasis: '100%',
    alignSelf: 'center',
    alignContent: 'baseline',
  },
  serial: (theme: any) => ({
    color: theme.palette.text.disabled,
  }),
  subHeader: (theme: any) => ({
    ...theme.typography['14/medium'],
    fontFamily: 'Inter',
    color: theme.palette.gray[400],
    backgroundColor: theme.palette.white.main,
    padding: '1rem',

    borderStyle: 'solid',
    borderColor: '#D9D9D9',
    borderWidth: '0 0 0.0625rem 0',
    top: 0,
    '& > *': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  }),
  placeholder: {
    width: '100%',
    marginLeft: 0,
  },
  segmentPlaceholder: () => ({
    width: '100%',
    margin: '0.0625rem 0',
  }),
  variant: {
    default: () => ({
      borderRadius: '1rem',
      padding: '1rem',
      width: '100%',
      margin: 0,
    }),
    static: (theme: any) => ({
      cursor: 'default',
      marginBottom: theme.spacing(1),
      '&:hover': {
        backgroundColor: 'transparent !important',
      },
    }),
    borderBetween: (theme: any) => ({
      padding: '1rem',
      '&:not(:first-of-type)': {
        position: 'relative',
        // 4px gap + 1px line + 4px gap, kept out of both rows' boxes so no
        // background or hover color can ever touch the line
        marginTop: '0.5625rem', // 9px
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-0.3125rem', // -(4px gap + 1px line)
          left: 0,
          right: 0,
          height: '0.0625rem', // 1px
          backgroundColor: theme.palette.border.main,
        },
      },
    }),
    simple: () => ({
      borderRadius: '1rem',
      padding: '1rem',
      '& + li': {
        marginTop: '0.5rem',
      },
    }),
    cartPositions: (theme: any) => ({
      backgroundColor: theme.palette.background.content,
      borderRadius: '0.5rem',
      padding: '1rem',
      marginBottom: '1rem',
      // flexDirection: 'column',
      '&:hover': {
        backgroundColor: theme.palette.background.content,
      },
      '> *': {
        height: '100%',
      },
    }),
    cartSummary: (theme: any) => ({
      paddingLeft: 0,
      paddingTop: 0,
      paddingRight: 0,
      paddingBottom: '1.5rem',
      marginBottom: '1.5rem',
      borderBottom: `0.0625rem solid ${theme.palette.gray.dark}`,
      '&:hover': {
        backgroundColor: theme.palette.background.content,
      },
      '> *': {
        height: '100%',
      },
      '&:last-child': {
        marginBottom: 0,
      },
    }),
    parts: () => ({
      justifyContent: 'space-between',
      padding: '1rem 0.5rem',
      cursor: 'auto',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    }),
  },
} as const

/**
 * Row styles, memoized per (variant, padding) pair.
 *
 * The row container previously applied its variant through an inline
 * `sx={theme => ({...})}` — a new function and object on every render, which
 * MUI/emotion cannot cache and therefore re-serializes for every row on every
 * render. As a module-scope styles function with primitive params, the styles
 * are serialized once per distinct (variant, padding) combination and all
 * rows share the resulting class.
 *
 * Numeric `padding` keeps its `sx` semantics (theme spacing units).
 *
 * @param theme           the application's current theme
 * @param options         the row style parameters
 * @param options.variant the row variant styles to apply
 * @param options.padding an optional padding override for the row
 * @returns               the row's styles
 */
const rowStyles = (
  theme: any,
  { variant, padding }: { variant?: string, padding?: number | string },
) => ({
  row: {
    ...((variant && sx.variant[variant]) || sx.variant.default)(theme),
    ...padding && {
      padding: typeof padding === 'number' ? theme.spacing(padding) : padding,
    },
    borderRadius: '0.5rem', // visible if hovered
    marginLeft: '0!important',
  },
  segment: {
    padding: 0,
  },
})

const scrollerStyles = (theme: any) => ({
  root: {
    background: 'transparent',
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.gray[800]} transparent`,

    marginLeft: `-${theme.layout.contentGap}`,
    marginRight: `-${theme.layout.contentGap}`,

    '& > div': {
      paddingLeft: theme.layout.contentGap,
      paddingRight: theme.layout.contentGap,
    },
  },
})

/**
 * Creates a memoized placeholder row element for each `segment` that has the placeholder
 * prop defined. Each row will be made by lazy-loading the element pointed by that property and some
 * props defined in the list's `placeholders` options.
 *
 * If no `segments` are passed or `unsegmented` is true, no elements will be returned.
 *
 * @param props              element's properties
 * @param props.segments     the list's segments configuration
 * @param props.placeholders an object to configure all rows
 * @param props.unsegmented  whether to ignore `segments`
 * @returns                  the list of placeholder elements
 */
const SegmentsPlaceholders = useMemoRef(
  ({ segments, unsegmented, height }: SegmentsPlaceholdersProps) =>
  // filtering all items from segments that have the placeholder property
    (segments && !unsegmented ? Object.keys(segments)
      .filter(segment => typeof segments[segment] === 'object'
      && (segments[segment] as { placeholder?: string }).placeholder)
      .map((segment, index) => {
        const config = segments[segment] as Exclude<SegmentOptions, string>
        const View = preload(config.placeholder as string)
        return <Grid
          item
          container
          key={index}
          component={MuiListItem}
          alignItems={'center'}
          sx={sx.segmentPlaceholder}
          // TODO: The string `segmentHeight` doesn't occur in the whole code base
          //       Obsolete?
          height={isArr(height) ? height[index] : height}
          padding={0}
          xs={12}
        >
          <View
            fullWidth
            size={12}
          />
        </Grid>
      }) : []),
  (props: SegmentsPlaceholdersProps) => [props.unsegmented],
)

/**
 * Creates a memoized placeholder row element. The placeholder will be made of cells according to
 * some props defined in `structure`, therefore, if some items from `structure` are
 * missing the placeholder (view path) prop, an empty array will be returned.
 *
 * @param props              element's properties
 * @param props.structure    an object defining the props of each row
 * @param props.placeholders an object to configure all rows
 * @param props.size         an array with the grid width of each cell
 * @param props.justify      the horizontal alignment the cells' content
 * @param props.spacing      the amount of space between each column
 * @returns                  the new placeholder row element
 */
const Placeholder = useMemoRef((
  { structure, placeholders = {}, ...props }: PlaceholderProps,
  ref: Ref<any>,
) => (
  <Grid
    ref={ref}
    item
    container
    component={props.component || MuiListItem}
    alignItems={'center'}
    justifyContent={props.justify || 'space-around'}
    columnSpacing={props.spacing}
    sx={{
      ...sx.variant.default(),
      ...sx.placeholder,
      height: placeholders.height,
    }}
  >
    {structure.filter(item => item.placeholder).map(({ placeholder: view, options }, i) => {
      const View = preload(view as string)
      return (
        <View
          {...placeholders}
          {...options?.placeholder}
          key={i}
          size={props.size?.[i] || options?.xs}
        />
      )
    })}
  </Grid>
), [])

/**
 * Creates `count` memoized placeholder row elements. Each row will be made of cells
 * according to some props defined in `structure`, therefore, if some items from
 * `structure` are missing the placeholder (view path) prop, an empty array is returned.
 *
 * @see Placeholder
 *
 * @param props       element's properties
 * @param props.count the number of placeholder rows to create
 * @returns           the list of placeholder row elements
 */
const Placeholders = useMemoRef((
  { count, structure, placeholders = {}, ...props }: PlaceholdersProps,
) => {
  // checking whether all items from structure have the placeholder property
  const missingView = structure.some(item => !item.placeholder)
  // if some element is missing it, we set the placeholder row's count to 0
  const placeholdersCount = missingView ? 0 : count ?? 0
  return repeat(placeholdersCount, (index: number) => (
    <Placeholder
      key={index}
      structure={structure}
      placeholders={placeholders}
      {...props}
    />
  ))
}, (props: PlaceholdersProps) => [props.count])

/**
 * Row component used to display data from an `item` created with {@link createItems}.
 *
 * @param props               element's properties
 * @param props.item          a data item processed by {@link createItems}
 * @param props.value         selected value. Its row will be selected
 * @param props.disabled      disabled state of the list. Can either be a boolean,
 *                            in which case the whole list will be disabled if
 *                            it's `true`, or it can be a list of ids, in
 *                            which case the corresponding rows will be disabled
 * @param props.variant       the name of the row variant styles
 * @param props.onClick       an event handler to execute whenever the row is clicked
 * @param props.onChange      an event handler to execute whenever the row is clicked.
 *                            if this handler is present, it will replace the
 *                            `onClick` handler. It's intended to be used if
 *                            the list should behave as a normal input field that has
 *                            a value that can be changed
 * @param props.onContextMenu an event handler to execute whenever the context menu is invoked
 * @param props.align         vertical alignment of the row's cells
 * @param props.justify       horizontal alignment of the row's cells
 * @param props.spacing       amount of spacing between each cell
 * @returns                   the new row element
 */
const Row = useMemoRef((
  { item, value, disabled, variant, align, justify, spacing, padding, ...props }: RowProps,
) => {
  const { onClick, onChange, onContextMenu } = props
  const classes = useStyles(rowStyles, { variant, padding })()
  const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    const clickEvent = new PlatformEvent(event, { key: item.key, item: item.data })

    onChange
      ? onChange(clickEvent)
      : onClick?.(clickEvent)
  }, [item, onClick, onChange])

  const isDisabled = disabled === true
    || (!!disabled && isArr(disabled) && disabled.includes(item.key))

  return (
    <Grid
      key={item.key}
      item
      container
      className={`${listRowClassName} ${item.data ? classes.row : classes.segment}`}

      disabled={isDisabled}
      aria-disabled={isDisabled}
      selected={!isDisabled && value === item.key}
      aria-selected={!isDisabled && value === item.key}

      component={MuiListItem}
      alignItems={align || 'center'}
      justifyContent={justify || 'space-around'}
      flexWrap={'nowrap'}
      {...item.data && {
        component: MuiListItemButton,
        onClick: handleClick,
        onContextMenu,
        columnSpacing: spacing,
      }}
    >
      <Suspense fallback={
        <div style={{ height: (item as any).placeholders?.height || DEFAULT_ROW_HEIGHT }} />
      }>
        {item?.cells?.map?.(({ View, cellProps, sizes }, index) => (
          <Grid
            item
            key={index}
            {...sizes}
            {...index === 0 && {
              // `columSpacing` on parent adds `paddingLeft` to each cell. To counteract
              // the visual difference in the first cell, it sets a negative `marginLeft` on the
              // row itself. This breaks our borderRadius on hover. Remove the padding from
              // the first cell and the negative margin in `rowStyles`
              sx: { paddingLeft: '0!important' },
            }}
          >
            <ErrorBoundary>
              <View {...cellProps} key={cellProps.key} /* events={transform(events)} */ />
            </ErrorBoundary>
          </Grid>
        ))}
      </Suspense>
    </Grid>
  )
}, (props: RowProps) => [props.item, props.value, props.disabled])

/**
 * Adds the parameters and handler necessary for a cell to be subscribed.
 *
 * @param caption      the cell subscription type
 * @param value        the item's value
 * @param options      the cell options
 * @param ref          the id of the item to subscribe to
 * @param events       an object that may contain the following event handlers
 * @param events.count the event handler to use when there is a `count` option
 * @param events.cell  the event handler to use when there is a `cell` option
 * @returns            the cell's subscription parameters
 * @private
 */
const _addSubParameters = (
  caption: 'count' | 'cell',
  value: unknown,
  options: unknown,
  ref: string | undefined,
  events: ListEvents,
) => ({
  value,
  options,
  ref,
  handler: events[caption],
})

/**
 * Returns all objects containing all values from `item` (i.e. from its value and refs
 * properties) that can also be found inside `keys`. If `keys` also contains a
 * `count` or a `cell` options, adds the subscription parameters to it.
 *
 * If there are no `keys`, the whole value of `item` is returned.
 *
 * @param item         an object containing data to extract from
 * @param keys         an object with keys matching those that should be extracted from item
 * @param events       an object that may contain the following event handlers
 * @param events.count the event handler to use when there is a `count` option
 * @param events.cell  the event handler to use when there is a `cell` option
 * @returns            the extracted model properties
 * @private
 */
export const _modelProps = (
  item: ListDataItem | null | undefined,
  keys: Record<string, boolean>,
  events: ListEvents,
) => {
  const { key, value = {}, refs = {} } = item || {}
  const optionKeys = Object.keys(keys)

  return optionKeys.length > 0
    ? optionKeys.reduce((agg: Record<string, ListDataItem | any>, option) => {
      agg[option] = option === 'count' || option === 'cell'
        ? _addSubParameters(option, value[option], keys[option], key, events)
        : value[option] ?? (refs && refs[option] && refs[option][0])
      return agg
    }, {})
    : { value }
}

/**
 * Sorts `a` and `b` by comparing some of their attributes, which should be defined in
 * `attributeNames`. Both elements are sorted by the first attribute in
 * `attributeNames` by which they differ, meaning that they will be first compared by the
 * first attribute in the `attributeNames` array, and will only be compared by the next one
 * if they have the same value for the first one.
 *
 * Returns 1 if `a` should go first, -1 if `b` should go first or 0 if their
 * attributes are equal.
 *
 * Can be used to sort arrays by passing the callback to their sort function.
 *
 * @param attributeNames the names of the attributes to compare by
 * @param depth          the position of the currently compared attribute
 * @returns              a sorting comparator function
 */
const attributeSort = (attributeNames: string[], depth = 0) =>
  (a: ListDataItem, b: ListDataItem): number => {
    const attributeName = attributeNames[depth]

    if (attributeName) {
      const attributeA = String(a?.value?.[attributeName] ?? '')
      const attributeB = String(b?.value?.[attributeName] ?? '')

      return attributeA.localeCompare(attributeB)
        || attributeSort(attributeNames, depth + 1)(a, b)
    }
    return 0
  }

/**
 * Returns a new array containing all items from `data` sorted by the attributes defined in
 * `segments`.
 *
 * @param data           the data to sort
 * @param props          the list's properties
 * @param props.segments the segment's configuration, which should be an object whose properties
 *                       have the names of the properties of `data` to compare
 * @returns              the sorted data
 */
const sortData = (data: ListDataItem[], { segments = {} }: ListProps): ListDataItem[] => {
  const attributeNames = Object.keys(segments)

  return [...data].sort(attributeSort(attributeNames))
}

/**
 * Virtually segments the list by adding header elements between the child elements that have a
 * different value for the property with which a segment template is declared. For that, the data
 * has to be already sorted by those properties.
 *
 * If there is a `segmentLabel` assigned to a value of a property, it will be passed to the
 * header element.
 *
 * @example declaration of segments in config:
 *  segments: {
 *    installedAt: 'Element/List/Segment/Organisation',
 *    locationExtension: 'Element/List/Segment/Title',
 *    placement: 'Element/List/Segment/Stripe',
 *  },
 *  segmentLabels: {
 *    placement: {
 *      'somewhere': 'The device is placed somewhere',
 *    }
 *  }
 *
 * Here, 'somewhere' should be a possible value for the placement property of each child, in which
 * case the header element would obtain its value as the label.
 *
 * @param items              the already sorted list of elements to segment
 * @param groupValues        the current values of the attributes used to segment the items
 * @param props              the list's properties
 * @param props.segments     an object containing pairs: property name - template's path
 * @param props.segmentLabel an object containing pairs: property name - label
 * @param props.unsegmented  whether to ignore `segments`
 * @returns                  a copy of `items` with the corresponding added segments
 */
const segmentItems = (
  items: ListItem[],
  groupValues: Record<string, any>,
  { segments = {}, segmentLabel = {}, unsegmented }: ListProps,
): ListItem[] => {
  const attributes = Object.keys(segments)

  return !attributes.length || unsegmented ? items : items.reduce((acc: ListItem[], item) => {
    const itemData = item.data

    attributes.forEach((attribute, index) => {
      const value = itemData?.value?.[attribute] || itemData?.refs?.[attribute]
      const lastValue = groupValues[attribute]
      const changed = def(value) && (value[0]?.key && lastValue?.[0]
        ? value[0].key !== lastValue[0].key
        : value !== lastValue)

      if (changed) {
        // clearing subordinate attribute values if the current one has changed
        Object.keys(groupValues)
          .slice(index)
          .forEach(key => delete groupValues[key])
        // updating current attribute value
        groupValues[attribute] = value
        // gathering segment's view and props
        const config = segments[attribute]
        const view = typeof config === 'object' ? config.view : config
        const View = preload(view)
        const key = `segment-${attribute}-${item.key}`
        const labels = segmentLabel[attribute]
        const cellProps = { value, labels }
        const sizes = { xs: 12 }
        const cell = { View, cellProps, sizes }
        const cells = [cell]
        const segment = { key, cells }

        acc.push(segment)
      }
    })
    acc.push(item)
    return acc
  }, [])
}

/**
 * Creates the items to be displayed as rows by the list. For that, it maps each object inside
 * `data` to a group of cell elements according to `props.structure`.
 *
 * @param data            data to display as list of cell groups
 * @param props           additional properties
 * @param props.structure view and props of each cell
 * @param props.size      grid width of each cell
 * @param props.events    event handlers available for the cells
 * @returns               the list of mapped data-structure items
 */
const createItems = (data: ListDataItem[], { structure, size, events }: ListProps): ListItem[] =>
  data.map((item) => {
    const cells = (item.value?.error && []) || structure.map(({ view, options }, cellIndex) => {
      const { keys = {}, xs, sm, md, lg, xl, ...rest } = options || {}

      const View = preload(view)
      const modelProps = _modelProps(item, keys, events)
      const cellProps = { ...modelProps, meta: item.meta, ...rest, events, id: item.key }
      const sizes = { xs: size?.[cellIndex] || xs, sm, md, lg, xl }

      return { View, cellProps, sizes }
    })

    return { key: item.key, data: item, cells }
  })

/**
 * Scroller
 *
 * @param props          the scroller's properties
 * @param props.children the scroller's content
 * @param props.style    Virtuoso's inline styles for the scroller
 * @param props.context  the list's context
 * @returns              the new scroller element
 */
const Scroller: Components<ListItem, ListContext>['Scroller'] = (
  { children, style, context, ...rest },
) => {
  const scrollClasses = useStyles(scrollerStyles)()
  return (
    <div
      {...rest}
      style={style}
      {...context.scrollPadding
        ? { className: scrollClasses.root }
        : {}}
    >
      {children}
    </div>
  )
}

/**
 * Footer
 *
 * @param props         the footer's properties
 * @param props.context the list's context
 * @returns             the placeholder footer element, or `null`
 */
const Footer: Components<ListItem, ListContext>['Footer'] = ({ context: ctx }) => {
  if (!ctx.pending && !ctx.loading) return null
  const { props } = ctx
  const fullCount = props.data?.length || props.placeholders?.count || props.limit
  // If we already have data only append 3 skeletons to the list. If we were to append
  // `fullCount`, the viewport would stay at the bottom, triggering `handleNext`
  // calls indefinitely.
  const count = ctx.hasData ? Math.min(3, fullCount as number) : fullCount
  return (
    <>
      {ctx.firstLoad && <SegmentsPlaceholders
        {...props}
        height={ctx.segmentHeight}
      />}
      <Placeholders
        {...props}
        count={count}
      />
    </>
  )
}

/**
 * EmptyPlaceholder
 *
 * @param props         the empty placeholder's properties
 * @param props.context the list's context
 * @returns             the no-data element, or `null`
 */
const EmptyPlaceholder: Components<ListItem, ListContext>['EmptyPlaceholder'] = (
  { context: ctx },
) => (
  !ctx.pending && !ctx.loading
    ? <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      {ctx.noDataPlaceholder}
    </div>
    : null
)

const components: Components<ListItem, ListContext> = {
  Scroller,
  Footer,
  EmptyPlaceholder,
}

/**
 * List component
 *
 * Makes use of Material UI's {@link List} components to display the information obtained by calling
 * `props.events.onOpen` as {@link ListItem} elements. The `props.structure` can
 * contain {@link ItemOptions} objects to be loaded and used as cells of each item.
 *
 * If calling `props.events.onOpen` returns a page, the component will place an observer at
 * the bottom of the list to trigger the next page load.
 *
 * If `props.events.list` and `props.events.onPublication` are defined, the
 * {@link useSockets} hook will use `props.events.list` and `props.documentType` to
 * subscribe to its channel.
 *
 * As there could be found no proper explanation about how to handle data using the {@link Suspense}
 * functionality, which doesn't work anymore as expected, this component implements its own suspense
 * mechanism by triggering the display of placeholders when the loading or the pending states are
 * true.
 *
 * @param props     the element's properties, see {@link ListProps}
 * @param props.ref a reference to the root element
 * @returns         the new list element
 */
const List = ({ ref: initialRef, ...props }: ListProps) => {
  const { data: startData, page: nextPage, unsegmented } = props
  const {
    children,
    events,
    action = true,
    value = null,
    disabled = false,
    overscanItemCount = 5,
    scrollPadding = true,
  } = props

  const {
    onOpen,
    onClick,
    onChange = undefined,
    onPage,
    onContextMenu,
    list,
    onPublication,
  } = events

  const placeholderCount = useMemo(
    () => props.limit ?? props.placeholders?.count ?? overscanItemCount ?? 5, [],
  )
  const rowHeight = useMemo(() =>
    props.rowHeight ?? props.placeholders?.height ?? DEFAULT_ROW_HEIGHT,
  [])
  const placeholderHeight = useMemo(() =>
    props.placeholders?.height ?? props.rowHeight ?? DEFAULT_ROW_HEIGHT,
  [])
  const segmentHeight = useMemo(
    () => props.placeholders?.segmentHeight ?? DEFAULT_SEGMENT_HEIGHT,
    [],
  )

  const [, startTransition] = useTransition()
  const [currentPage, setCurrentPage] = useState<string | null>(null)
  const [data, setData] = useState<ListDataItem[]>([]) // raw data obtained from the server
  const [items, setItems] = useState<ListItem[]>([]) // modified data (to be easily displayed)
  const [updateKey, forceUpdate] = useState(0)
  const [loading, setLoading] = useState(true)
  const [firstLoad, setFirstLoad] = useState(true)
  const [connect] = useState(!!list && !!onPublication)
  const [autoHeight, setAutoHeight] = useState(placeholderCount * placeholderHeight)
  const ref = useRef<VirtuosoHandle>(initialRef?.current ?? initialRef)
  const groupValuesRef = useRef<Record<string, any>>({})
  const scrollRef = useRef<StateSnapshot>(props.scrollPosition || null)
  const isFetchingRef = useRef(false)

  const createItemsFromData = props.iterator || createItems

  const pagination = usePagination(
    onOpen,
    startData,
    nextPage,
    currentPage,
    setCurrentPage,
    [updateKey],
  )
  const { data: totalData }: { data: ListDataItem[] } = connect
    ? useSockets(data, props as unknown as { list: Function, onPublication: Function }, [updateKey])
    : { data }
  const { data: pageData, page, pending, loadNext } = pagination

  const hasData = totalData.length > 0 || pageData?.length > 0 || data?.length > 0

  useEffect(() => {
    if (props.update) {
      startTransition(() => {
        // Scenario: Both `startData` and `update` are set. This means we want
        // to rerender the list with OUR new data, but don't do this on first load, because
        // on first load, usePagination will be executed and return pageData as well, this
        // will cause duplicate key errors
        if (startData && !firstLoad) {
          const newItems = createItemsFromData(startData, props)
          const processedItems = segmentItems(newItems, groupValuesRef.current, props)
          setData(startData)
          setItems(processedItems)
        } else {
          setItems(items.map(item => ({ ...item })))
        }
      })
    }
  }, [props.update])

  useEffect(() => {
    if (props.reload && !firstLoad) {
      startTransition(() => {
        setCurrentPage(null)
        forceUpdate(n => n + 1)
        setData([])
        setItems([])
      })
    }
  }, [props.reload])

  useEffect(() => {
    if (data !== totalData) {
      startTransition(() => {
        groupValuesRef.current = {}
        const sortedData = sortData(totalData, props)
        const newItems = createItemsFromData(sortedData, props)
        const processedItems = segmentItems(newItems, groupValuesRef.current, props)
        setData(sortedData)
        setItems(processedItems)
      })
    }
  }, [totalData])

  useEffect(() => {
    // setting `loading` to `true` unconditionally will render skeletons to the list.
    // This can, on fast scrolls, re-trigger a `onEndReached` execution. Only apply
    // it if we are indeed fetching (`pending`).
    if (pending) {
      setLoading(true)
      return
    }

    const newItems = createItemsFromData(pageData, props)
    const processedItems = segmentItems(newItems, groupValuesRef.current, props)
    startTransition(() => {
      setData(prevData => prevData.concat(pageData))
      setItems(prevItems => prevItems.concat(processedItems))
      setLoading(false)
      setFirstLoad(false)
    })

    isFetchingRef.current = false
  }, [pageData, pending])

  const handleAutoHeight = useCallback((h: number) => { h > 0 && setAutoHeight(h) }, [])

  // We need a ref here to track if we're currently fetching a new page until the new chunk has
  // been applied in the useEffect above. Tracking `loading`/`pending` isn't enough. `onEndReached`
  // will trigger again before `loading` has been set to `true` (on really fast scrolls), triggering
  // a loop.
  const handleNext = useCallback(() => {
    if (isFetchingRef.current || pending || !page) return
    isFetchingRef.current = true
    setLoading(true)
    loadNext()
  }, [pending, page, loadNext])

  const handleScroll = useCallback(
    (_: React.UIEvent<HTMLDivElement>) => {
      ref?.current?.getState((listState) => {
        scrollRef.current = listState
        onPage?.(new PlatformEvent('page', { data, page, unsegmented, scrollPosition: scrollRef.current }))
      })
    },
    [data, page],
  )

  useEffect(() => {
    onPage?.(new PlatformEvent('page', { data, page, unsegmented, scrollPosition: scrollRef.current }))
  }, [data])

  const noDataPlaceholder = useMemo(() => cloneElement(
    props.noData as ReactElement<{ fromSearch?: boolean }>,
    { fromSearch: props.fromSearch },
  ), [props.fromSearch])

  const shouldHide = useMemo(
    () => !hasData && !loading && !pending && props.autoHide,
    [hasData, loading, pending, props.autoHide],
  )

  // if `props.scrollPosition` is truthy we want to restore the list state because we went
  // back to the list from a detail screen. We have to prevent rendering until `items`
  // is populated. If we don't do this Virtuoso renders with 0 items on initial mount
  // and our previous state gets discarded and the estimated item height is used,
  // this will cause a wrong scroll position
  const isRestoring = useMemo(
    () => !!props.scrollPosition && items.length === 0 && (loading || pending),
    [props.scrollPosition, items.length, loading, pending],
  )

  const keyExtractor = useCallback(
    (index: number, item: ListItem) => (item.key ? String(item.key) : index),
    [],
  )

  const Headers = useCallback(({ isLoading }: { isLoading?: boolean }) => (
    <>
      <Header
        view={props.header}
        label={props.headerLabel}
        hidden={!props.header || isLoading}
      />
      <SubHeader
        label={props.label}
        justify={props.justify}
        size={props.size}
        hidden={!props.label}
        sx={sx.subHeader}
      />
    </>
  ), [])

  const renderItem = useCallback((index: number, item: ListItem, ctx: any) => (
    <Row
      key={item.key}
      item={item}

      value={ctx.value}
      disabled={ctx.disabled}

      variant={props.variant}
      // if "action && ..." is used it will default to "false" if action is not defined
      onClick={action ? onClick : undefined}
      onChange={onChange}
      onContextMenu={onContextMenu?.(item.data)}
      justify={props.justify}
      padding={props.padding}
      align={props.align}
      spacing={props.spacing}
    />
  ), [])

  const context = useMemo(
    () => ({
      value,
      disabled,
      pending,
      loading,
      hasData,
      firstLoad,
      segmentHeight,
      props,
      noDataPlaceholder,
      scrollPadding,
    }),
    [
      value, disabled, pending, loading, hasData, firstLoad, segmentHeight,
      noDataPlaceholder, scrollPadding,
    ],
  )

  return shouldHide ? null : (
    <ErrorBoundary>
      <Grid
        container
        key={'container'}
        wrap={'nowrap'}
        direction={'column'}
        sx={{ height: '100%' }}
      >
        {hasData && <Headers isLoading={loading} />}
        {isRestoring ? (
          <>
            {firstLoad && (
              <SegmentsPlaceholders
                {...props}
                height={segmentHeight}
              />
            )}
            <Placeholders
              {...props}
              count={placeholderCount}
            />
          </>
        ) : (
          <Virtuoso
            ref={ref}

            data={items}
            totalCount={items.length}

            defaultItemHeight={rowHeight}
            // only set if props.rowHeight is explicitly set
            {...props.rowHeight && {
              fixedItemHeight: props.rowHeight
            }}
            // fixedItemHeight={props.rowHeight}
            restoreStateFrom={props.scrollPosition ?? undefined}

            endReached={handleNext}
            itemContent={renderItem}
            onScrollEnd={handleScroll}
            style={{
              scrollbarWidth: 'thin',
              ...props.autoHeight
                ? { height: autoHeight }
                : { flex: 1, minHeight: 0 },
            }}
            totalListHeightChanged={
              props.autoHeight
                ? handleAutoHeight
                : undefined
            }
            context={context}
            computeItemKey={keyExtractor}
            components={components}

            {...overscanItemCount ? {
              minOverscanItemCount: { top: overscanItemCount, bottom: overscanItemCount },
              increaseViewportBy: {
                top: overscanItemCount * (props?.placeholders?.height ?? rowHeight),
                bottom: overscanItemCount * (props?.placeholders?.height ?? rowHeight),
              },
            } : {}}
          />
        )}
      </Grid>
      {children}
    </ErrorBoundary>
  )
}

export default useMemoRef(NoDataHOC(List), (props: ListProps) => [
  props.update,
  props.reload,
  props.value,
  props.disabled,
])
